import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { formatPrice, STAGE_PRICES } from '../utils/currency';

const Stage2Modal = ({ isOpen, onClose, onOpenContactForm }) => {
  const { t, language } = useLanguage();

  const email = process.env.REACT_APP_CONTACT_EMAIL || 'meller@racing.hub.pl';
  const phone = process.env.REACT_APP_CONTACT_PHONE || '+48 731 662 005';
  const address = process.env.REACT_APP_ADDRESS_STREET || 'Świętego Jana 13';
  const postalCode = process.env.REACT_APP_ADDRESS_POSTAL || '43-267';
  const city = process.env.REACT_APP_ADDRESS_CITY || 'Suszec';
  const country = process.env.REACT_APP_ADDRESS_COUNTRY || 'Poland';
  const nip = process.env.REACT_APP_NIP || 'NIP: 6381866164';
  const facebookUrl = process.env.REACT_APP_FACEBOOK_URL || 'https://www.facebook.com/p/Meller-Racing-warsztat-i-hamownia-motocyklowa-100083175588341/';
  const instagramUrl = process.env.REACT_APP_INSTAGRAM_URL || 'https://www.instagram.com/meller_racing_/';
  const googleMapsUrl = 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2560.4944798658795!2d18.7819575!3d50.0337799!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4716b39fd6f7b977%3A0xfc21082ddd12aaf8!2sMeller%20Racing%2Fhamownia%20motocyklowa%20i%20serwis!5e0!3m2!1sen!2spl!4v1642000000000!5m2!1sen!2spl';

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/60 z-40"
          />
          
          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
          >
            <div className="bg-gray-900 border border-gray-700 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col">
              {/* Header */}
              <div className="bg-gradient-to-r from-racing-red to-red-700 p-6 flex items-center justify-between border-b border-red-900 flex-shrink-0">
                <div>
                  <h2 className="text-2xl md:text-3xl font-bold text-white">
                    {t('products.stage2Title') || 'Stage 2'}
                  </h2>
                  <p className="text-red-100 mt-1">
                    {formatPrice(STAGE_PRICES.stage2, language)}
                  </p>
                </div>
                <button
                  onClick={onClose}
                  className="text-white hover:bg-red-800 rounded-lg p-2 transition-all"
                  aria-label="Close"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Content */}
              <div className="p-6 md:p-8 overflow-y-auto flex-1">
                <div className="mb-8">
                  <h3 className="text-lg font-semibold text-white mb-3">{t('contact.getInTouch')}</h3>
                </div>

                <div className="space-y-6">

            {/* Email */}
            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 bg-racing-red rounded-full flex items-center justify-center flex-shrink-0">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <div>
                <h4 className="font-semibold text-white mb-1">{t('contact.email')}</h4>
                <a href={`mailto:${email}`} className="text-racing-red hover:text-orange-500 break-all transition">
                  {email}
                </a>
              </div>
            </div>

            {/* Phone */}
            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 bg-racing-red rounded-full flex items-center justify-center flex-shrink-0">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
              </div>
              <div>
                <h4 className="font-semibold text-white mb-1">{t('contact.phone')}</h4>
                <a href={`tel:${phone.replace(/\s/g, '')}`} className="text-racing-red hover:text-orange-500 transition">
                  {phone}
                </a>
              </div>
            </div>

            {/* Address */}
            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 bg-racing-red rounded-full flex items-center justify-center flex-shrink-0">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <div>
                <h4 className="font-semibold text-white mb-1">{t('contact.address')}</h4>
                <p className="text-gray-300">
                  {address}<br />
                  {postalCode} {city}, {country}<br />
                  {nip}
                </p>
              </div>
            </div>

            {/* Social Media */}
            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 bg-racing-red rounded-full flex items-center justify-center flex-shrink-0">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <div>
                <h4 className="font-semibold text-white mb-2">{t('contact.socialMedia')}</h4>
                <div className="flex space-x-4">
                  <a
                    href={facebookUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-racing-red hover:text-orange-500 transition"
                  >
                    <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                    </svg>
                  </a>
                  <a
                    href={instagramUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-racing-red hover:text-orange-500 transition"
                  >
                    <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                    </svg>
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Google Maps Section */}
          <div className="mt-8 pt-6 border-t border-gray-700">
            <h3 className="text-lg font-semibold text-white mb-4">{t('contact.visitUsInPerson')}</h3>
            <div className="w-full h-64 rounded-lg overflow-hidden border border-gray-700">
              <iframe
                src={googleMapsUrl}
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Meller Racing Location"
              ></iframe>
            </div>
          </div>

          {/* Buttons */}
          <div className="mt-8 pt-6 border-t border-gray-700 flex-shrink-0">
            <div className="flex gap-3">
              <button
                onClick={onOpenContactForm}
                className="flex-1 bg-racing-red hover:bg-red-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors"
              >
                {t('stage1Modal.contactButton') || 'Contact Us'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default Stage2Modal;
